import { Link } from "react-router-dom";

const CheckIcon = ({
  className = "w-4 h-4 text-green-500 mt-0.5 flex-shrink-0",
}) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

export const SchwabIntegrationPage = () => {
  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-green-800 via-green-700 to-teal-700 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center bg-white/10 border border-white/20 px-4 py-2 rounded-full text-sm font-medium mb-6">
              Powered by Charles Schwab API
            </div>

            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Schwab <span className="text-green-200">Integration</span>
            </h1>

            <p className="text-xl text-green-100 mb-8 leading-relaxed">
              Spread Genius connects to your Charles Schwab account to bring
              your live portfolio, real-time options data, and trade execution
              directly into one intelligent platform — securely and with your
              full control.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/products/spread-genius"
                className="bg-white text-green-800 px-8 py-3 rounded-lg font-bold hover:bg-green-50 transition-colors inline-flex items-center justify-center"
              >
                See Spread Genius Features
                <svg
                  className="w-4 h-4 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white/50 text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors inline-flex items-center justify-center"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What it unlocks */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Connecting Schwab Unlocks
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The free tier analyzes your trade history from CSV exports.
              Connect your Schwab account and everything becomes live.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: "📊",
                title: "Live Portfolio Positions",
                desc: "See your real-time equity and options positions, unrealized P&L, and delta exposure — pulled directly from your Schwab account.",
              },
              {
                icon: "🎯",
                title: "Covered Call Scanner",
                desc: "Automatically scans your equity positions against live option chains to surface the best covered-call opportunities by yield and delta.",
              },
              {
                icon: "⚠️",
                title: "Risk Radar",
                desc: "A visual risk dashboard across your entire portfolio — concentration heatmap, expiry calendar, and net delta exposure at a glance.",
              },
              {
                icon: "📉",
                title: "Cash-Secured Put Scoring",
                desc: "Ranks CSP opportunities on your watchlist using live IV Rank, support levels, and earnings proximity.",
              },
              {
                icon: "⚡",
                title: "High-Conviction Signals",
                desc: "Fibonacci-based entry signals across your personal watchlist using live price data from Schwab Market Data.",
              },
              {
                icon: "📋",
                title: "Order Preview & Placement",
                desc: "Preview and place covered-call orders directly from the scanner. You confirm every order before it's submitted — no automation.",
              },
            ].map(({ icon, title, desc }) => (
              <div
                key={title}
                className="bg-gray-50 border border-gray-200 rounded-xl p-6"
              >
                <div className="text-3xl mb-4">{icon}</div>
                <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How auth works */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                You're Always in Control
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Connecting your Schwab account uses Schwab's own secure login
                page — Spread Genius never sees your Schwab username or
                password. You authorize the connection directly with Schwab, and
                you can revoke it at any time.
              </p>
              <p className="text-gray-600 mb-8">
                This is the same OAuth standard used by major financial apps.
                Schwab shows you exactly what Spread Genius is requesting before
                you approve.
              </p>

              <div className="space-y-5">
                {[
                  {
                    step: "1",
                    title: 'Click "Connect Schwab Account"',
                    desc: "You're taken to Schwab's own login page at schwab.com.",
                  },
                  {
                    step: "2",
                    title: "Log in directly with Schwab",
                    desc: "Your credentials go only to Schwab — Spread Genius never sees them.",
                  },
                  {
                    step: "3",
                    title: "Approve the connection",
                    desc: "Schwab shows you what Spread Genius is requesting. You approve (or decline).",
                  },
                  {
                    step: "4",
                    title: "You're connected",
                    desc: "Spread Genius receives a secure token from Schwab. Your premium features activate instantly.",
                  },
                ].map(({ step, title, desc }) => (
                  <div key={step} className="flex gap-5">
                    <div className="w-9 h-9 bg-green-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0 text-sm">
                      {step}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-0.5">
                        {title}
                      </h4>
                      <p className="text-gray-500 text-sm">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
              <h3 className="font-bold text-gray-900 mb-6 text-lg">
                Disconnect anytime
              </h3>
              <p className="text-gray-600 text-sm mb-6">
                You can revoke Spread Genius's access from two places — inside
                the app, or directly from your Schwab account settings. Either
                way, access is cut off immediately.
              </p>
              <div className="space-y-3">
                {[
                  "Disconnect from Spread Genius settings",
                  "Revoke from Schwab account → Authorized Apps",
                  "Access is revoked instantly, no data retained",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckIcon />
                    <span className="text-gray-700 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Data access */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              What We Access — and What We Don't
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We only request the minimum access needed to deliver each feature.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Read */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                </div>
                <span className="font-bold text-gray-900">What we read</span>
              </div>
              <ul className="space-y-3">
                {[
                  "Live positions and balances",
                  "Closed trade history",
                  "Real-time option chain quotes",
                  "Equity price quotes",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2 text-sm text-gray-600"
                  >
                    <CheckIcon className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Write */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-orange-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-orange-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </div>
                <span className="font-bold text-gray-900">What we can do</span>
              </div>
              <ul className="space-y-3 mb-6">
                {[
                  "Preview covered-call orders (read-only preview, no execution)",
                  "Place orders you explicitly confirm — one at a time",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2 text-sm text-gray-600"
                  >
                    <CheckIcon className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="bg-orange-50 rounded-lg p-3 text-xs text-orange-700">
                No automated or algorithmic trading. Every order requires your
                explicit confirmation.
              </div>
            </div>

            {/* Never */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-red-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-red-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                    />
                  </svg>
                </div>
                <span className="font-bold text-gray-900">
                  What we never access
                </span>
              </div>
              <ul className="space-y-3">
                {[
                  "Your Schwab login credentials",
                  "Full account numbers",
                  "Tax documents or statements",
                  "Personal identity documents",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2 text-sm text-gray-600"
                  >
                    <svg
                      className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy commitments */}
      <section className="py-20 bg-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">
              Our Privacy Commitments
            </h2>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto">
              Your brokerage data is yours. We treat it accordingly.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: "🔐",
                title: "No Credential Storage",
                desc: "You log in at schwab.com. We only receive the secure token Schwab issues — never your username or password.",
              },
              {
                icon: "🛡️",
                title: "Encrypted at Rest",
                desc: "All tokens are stored encrypted using AWS-managed keys. Never in plaintext, never on the frontend.",
              },
              {
                icon: "🚫",
                title: "No Data Selling",
                desc: "Your brokerage data is never sold, shared with third parties, or used for anything beyond your own session.",
              },
              {
                icon: "🗑️",
                title: "User-Controlled",
                desc: "Disconnect at any time from within Spread Genius or directly from your Schwab account settings.",
              },
            ].map(({ icon, title, desc }) => (
              <div
                key={title}
                className="bg-white/5 border border-white/10 rounded-xl p-6"
              >
                <div className="text-3xl mb-4">{icon}</div>
                <h3 className="font-semibold text-white mb-2">{title}</h3>
                <p className="text-slate-300 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Questions About Our Schwab Integration?
          </h2>
          <p className="text-xl text-gray-600 mb-10">
            We welcome questions from users, Schwab's developer team, or
            compliance reviewers. Reach out and we'll respond promptly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-green-700 text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-green-600 transition-colors inline-flex items-center justify-center"
            >
              Get In Touch
              <svg
                className="w-5 h-5 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </Link>
            <Link
              to="/products/spread-genius"
              className="border-2 border-gray-300 text-gray-700 px-10 py-4 rounded-lg font-bold text-lg hover:border-gray-400 transition-colors inline-flex items-center justify-center"
            >
              View Spread Genius
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
